/* eslint-disable no-undef */

import { cssThemeLifecycles } from './cssThemeLifecycles'
import { AppProps, LifeCycleFn } from 'single-spa'

import 'systemjs-webpack-interop/auto-public-path'

interface ThemeLifecycles<ExtraProps> {
  bootstrap: LifeCycleFn<ExtraProps>
  mount: LifeCycleFn<ExtraProps>
  unmount: LifeCycleFn<ExtraProps>
}

export interface IThemeManager<ExtraProps> {
  getLifecycles(): ThemeLifecycles<ExtraProps>
  cssAssetPaths(): string[]
  getCssAssets(props: ExtraProps): string[]
}

/**
 * ThemeManager
 * The ThemeManager class is used to manage the themes for the application, it works in conjunction with the ExposeRuntimeCssAssetsPlugin
 * banquet-expose-runtime-css-assets-webpack-plugin and cssThemesLifecycle to control the css assets that are loaded for the application.
 *
 * @param name string | ((props: Props) => string)
 * @param theme { name: AvilableTheme | ((props: Props) => AvilableTheme) }
 *
 * @returns ThemeManager
 */

export class ThemeManager<Props extends AppProps>
  implements IThemeManager<Props>
{
  protected theme!: { name: string | ((props: Props) => string) }
  protected additionalCSSAssets: string[] = []

  constructor(name: string | ((props: Props) => string), cssAssets?: string[])
  constructor(
    theme: { name: string | ((props: Props) => string) },
    cssAssets?: string[]
  )
  constructor(...args: any[]) {
    if (typeof args[0] === 'string' || typeof args[0] === 'function') {
      this.theme = { name: args[0] as string }
    } else if (typeof args[0] === 'object') {
      this.theme = args[0]
    }

    if (args[1]) {
      this.additionalCSSAssets = args[1]
    }

    // this ensures that ExposeRuntimeCssAssetsPlugin is configured in webpacks
    if (!__webpack_require__.cssAssets) {
      throw Error(
        `banquet-theme-manager: to use webpackExtractedCss, add ExposeRuntimeCssAssetsPlugin to your webpack config.`
      )
    }
  }

  /**
   * getLifecycles method is cal  led within the banquetSingleSpaReact function
   * @returns cssThemeLifecycles
   */
  public getLifecycles() {
    const { bootstrap, mount, unmount } = cssThemeLifecycles<Props>({
      getCssAssets: (props: Props) => this.getCssAssets(props),
      shouldUnmount: true,
      timeout: 5000,
      createLink: (url: string) => {
        const linkEl = document.createElement('link')
        linkEl.href = url
        linkEl.rel = 'stylesheet'
        return linkEl
      }
    })
    return {
      bootstrap,
      mount,
      unmount
    }
  }

  /**
   * cssAssetPaths
   * @returns css asset paths
   */
  public cssAssetPaths() {
    return [
      ...__webpack_require__.cssAssets.map((fileName: string) => {
        return __webpack_public_path__ + fileName
      })
    ]
  }

  /**
   * getTheme method is called within the banquetSingleSpaCss themes
   * main is the file name of the main css file
   * @param props BanquetProps
   * @returns css asset url
   */
  public getCssAssets(props: Props) {
    const result: string[] = []
    result.push(...this.additionalCSSAssets)
    // setup main css file - always included, all import './index.css' will be included in main
    if (__webpack_require__.cssAssets.includes('main')) {
      result.push(this.getCssAssetName('main'))
    }
    if (typeof this.theme.name === 'function') {
      const name = this.theme.name(props)
      result.push(this.getCssAssetName(name))
    } else {
      result.push(this.getCssAssetName(this.theme.name))
    }

    return result
  }

  /**
   * getCssAssetName
   * @param name
   * @returns
   */
  private getCssAssetName(name: string) {
    try {
      return (
        __webpack_public_path__ + __webpack_require__.cssAssetFileName(name)
      )
    } catch (e) {
      throw Error(
        `banquet-theme-manager: theme ${name} not found in cssAssets. Make sure the theme is in the cssAssets array.`
      )
    }
  }
}
